import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Loader2, Printer, Search, AlertTriangle } from 'lucide-react';
import { formatQuantidade } from '../utils/numberFormat';
import { baixarRelatorioProducaoPdf } from '../utils/relatorioProducaoPdf';

const TZ = 'America/Sao_Paulo';
const hojeStr = () => new Date().toLocaleDateString('en-CA', { timeZone: TZ });

function fmtDataHora(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('pt-BR', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function fmtDataProducao(ymd) {
  const s = String(ymd || '').slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
}
function fotosDaLinha(l) {
  const lista = Array.isArray(l.fotos_etapa) ? l.fotos_etapa.filter((f) => f && f.url) : [];
  if (l.foto_url && !lista.some((f) => f.url === l.foto_url)) {
    lista.unshift({ nome: 'Foto', url: l.foto_url });
  }
  return lista;
}
function fmtDuracao(seg) {
  if (seg == null) return '—';
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return `${m}min`;
}
function fmtParametros(parametros) {
  if (!Array.isArray(parametros) || parametros.length === 0) return '';
  return parametros
    .map((p) => {
      const nome = p.nome || p.label || p.chave || '';
      const valor = p.valor != null ? p.valor : p.value != null ? p.value : '';
      const un = p.unidade || p.unit || '';
      return `${nome}: ${valor}${un ? ' ' + un : ''}`;
    })
    .filter(Boolean)
    .join(' · ');
}

const RelatorioProducaoPage = () => {
  const { getEmpresaId } = useAuth();
  const empresaId = getEmpresaId();

  const [setores, setSetores] = useState([]);
  const [filtros, setFiltros] = useState({
    data_inicio: hojeStr(),
    data_fim: hojeStr(),
    setor_id: '',
    lote: '',
  });
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [buscou, setBuscou] = useState(false);
  const [pdfBusy, setPdfBusy] = useState('');

  useEffect(() => {
    if (!empresaId || empresaId === 'default') return;
    httpsCallable(functions, 'listSetores')({ empresa_id: empresaId, apenas_ativos: true })
      .then((r) => setSetores(r.data?.data?.items || []))
      .catch(() => {});
  }, [empresaId]);

  const buscar = useCallback(async () => {
    if (!empresaId || empresaId === 'default') {
      setErro('Empresa não identificada.');
      return;
    }
    setLoading(true);
    setErro('');
    try {
      const r = await httpsCallable(functions, 'getRelatorioProducao')({
        empresa_id: empresaId,
        data_inicio: filtros.data_inicio,
        data_fim: filtros.data_fim,
        setor_id: filtros.setor_id,
        lote: filtros.lote,
      });
      setItens(r.data?.data?.items || []);
      setBuscou(true);
    } catch (e) {
      setErro(e.message || 'Erro ao gerar o relatório.');
    } finally {
      setLoading(false);
    }
  }, [empresaId, filtros]);

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Agrupa por setor
  const grupos = useMemo(() => {
    const m = new Map();
    for (const it of itens) {
      const k = it.setor_nome || it.setor_id || '—';
      const arr = m.get(k) || [];
      arr.push(it);
      m.set(k, arr);
    }
    return [...m.entries()];
  }, [itens]);

  const totalEntrada = itens.reduce((s, i) => s + (Number(i.quantidade_entrada) || 0), 0);
  const totalSaida = itens.reduce((s, i) => s + (Number(i.quantidade_saida) || 0), 0);
  const totalPerda = itens.reduce((s, i) => s + (Number(i.perda) || 0), 0);

  const emitirPdf = async (comFotos) => {
    if (!itens.length) {
      setErro('Gere o relatório antes de exportar o PDF.');
      return;
    }
    setPdfBusy(comFotos ? 'fotos' : 'sem');
    setErro('');
    try {
      const setorNome = filtros.setor_id ? setores.find((s) => s.id === filtros.setor_id)?.nome || '' : '';
      const sufixo = comFotos ? 'com-fotos' : 'sem-fotos';
      await baixarRelatorioProducaoPdf({
        grupos,
        periodo: `${fmtDataProducao(filtros.data_inicio)} a ${fmtDataProducao(filtros.data_fim)}`,
        setorFiltro: setorNome,
        totais: {
          registros: itens.length,
          entrada: totalEntrada,
          saida: totalSaida,
          perda: totalPerda,
        },
        comFotos,
        nomeArquivo: `relatorio-producao_${filtros.data_inicio}_a_${filtros.data_fim}_${sufixo}.pdf`,
      });
    } catch (e) {
      setErro(e.message || 'Não foi possível gerar o PDF.');
    } finally {
      setPdfBusy('');
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <FileText className="h-6 w-6 text-emerald-600" /> Relatórios de Produção
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Substitui as planilhas de papel: início, fim, duração, operador, parâmetros, entrada/saída e perdas por setor.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => emitirPdf(false)}
            disabled={!!pdfBusy || !itens.length}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {pdfBusy === 'sem' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            PDF sem fotos
          </button>
          <button
            type="button"
            onClick={() => emitirPdf(true)}
            disabled={!!pdfBusy || !itens.length}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {pdfBusy === 'fotos' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            PDF com fotos
          </button>
        </div>
      </div>

      {/* Filtros */}
      <section className="grid gap-3 rounded-xl border bg-white p-4 sm:grid-cols-2 lg:grid-cols-5 print:hidden">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">De</label>
          <input
            type="date"
            value={filtros.data_inicio}
            onChange={(e) => setFiltros((f) => ({ ...f, data_inicio: e.target.value }))}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Até</label>
          <input
            type="date"
            value={filtros.data_fim}
            onChange={(e) => setFiltros((f) => ({ ...f, data_fim: e.target.value }))}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Setor</label>
          <select
            value={filtros.setor_id}
            onChange={(e) => setFiltros((f) => ({ ...f, setor_id: e.target.value }))}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            {setores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Lote</label>
          <input
            type="text"
            value={filtros.lote}
            onChange={(e) => setFiltros((f) => ({ ...f, lote: e.target.value }))}
            placeholder="Ex: 159/2026"
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={buscar}
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Gerar
          </button>
        </div>
      </section>

      {erro && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 print:hidden">
          {erro}
        </div>
      )}

      {/* Cabeçalho de impressão */}
      <div className="hidden print:block">
        <h2 className="text-lg font-bold">Relatório de Produção</h2>
        <p className="text-sm">
          Período: {filtros.data_inicio} a {filtros.data_fim}
          {filtros.setor_id ? ` · Setor: ${setores.find((s) => s.id === filtros.setor_id)?.nome || ''}` : ''}
        </p>
      </div>

      {/* Resumo */}
      {buscou && (
        <section className="grid gap-3 sm:grid-cols-4">
          <div className="rounded-xl border bg-white p-4">
            <p className="text-xs text-gray-500">Registros</p>
            <p className="text-xl font-bold text-gray-900">{itens.length}</p>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <p className="text-xs text-gray-500">Entrada total</p>
            <p className="text-xl font-bold text-gray-900">{formatQuantidade(totalEntrada)}</p>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <p className="text-xs text-gray-500">Saída total</p>
            <p className="text-xl font-bold text-gray-900">{formatQuantidade(totalSaida)}</p>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <p className="text-xs text-gray-500">Perda total</p>
            <p className="text-xl font-bold text-red-600">{formatQuantidade(totalPerda)}</p>
          </div>
        </section>
      )}

      {/* Tabelas por setor */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Gerando relatório…
        </div>
      ) : buscou && grupos.length === 0 ? (
        <p className="rounded-lg bg-white px-4 py-6 text-center text-sm text-gray-400">
          Nenhum apontamento no período/filtros selecionados.
        </p>
      ) : (
        grupos.map(([setorNome, linhas]) => (
          <section key={setorNome}>
            <h2 className="mb-2 text-lg font-semibold text-gray-900">{setorNome}</h2>
            <div className="overflow-x-auto rounded-xl border bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Data</th>
                    <th className="px-3 py-2">Lote</th>
                    <th className="px-3 py-2">Etapa</th>
                    <th className="px-3 py-2">Operador</th>
                    <th className="px-3 py-2">Início</th>
                    <th className="px-3 py-2">Fim</th>
                    <th className="px-3 py-2">Duração</th>
                    <th className="px-3 py-2 text-right">Entrada</th>
                    <th className="px-3 py-2 text-right">Saída</th>
                    <th className="px-3 py-2 text-right">Peso final</th>
                    <th className="px-3 py-2 text-right">Perda</th>
                    <th className="px-3 py-2">Parâmetros</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr key={l.id} className={`border-t ${l.tipo === 'perda' ? 'bg-red-50' : ''}`}>
                      <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">
                        {fmtDataProducao(l.data_producao)}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900">{l.lote_fabricacao || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {l.tipo === 'perda' ? (
                          <span className="inline-flex items-center gap-1 text-red-600">
                            <AlertTriangle className="h-3.5 w-3.5" /> Perda
                          </span>
                        ) : l.tipo === 'parametro' ? (
                          <span className="text-blue-700">Amostra{l.etapa_nome ? ` · ${l.etapa_nome}` : ''}</span>
                        ) : (
                          l.etapa_nome || '—'
                        )}
                        {l.equipamento_nome ? <span className="text-gray-400"> · {l.equipamento_nome}</span> : ''}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{l.operador_nome || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDataHora(l.tempo_inicio_ms)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDataHora(l.tempo_fim_ms)}</td>
                      <td className="px-3 py-2 text-gray-600">{fmtDuracao(l.duracao_segundos)}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatQuantidade(l.quantidade_entrada, '—')}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatQuantidade(l.quantidade_saida, '—')}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900">
                        {l.peso_final != null && l.peso_final !== '' ? `${formatQuantidade(l.peso_final)} kg` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-red-600">{l.perda ? formatQuantidade(l.perda) : ''}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {fmtParametros(l.parametros)}
                        {l.observacao ? <div className="text-amber-700">{l.observacao}</div> : null}
                        {fotosDaLinha(l).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {fotosDaLinha(l).map((f, i) => (
                              <a
                                key={`${l.id}-foto-${i}`}
                                href={f.url}
                                target="_blank"
                                rel="noreferrer"
                                className="block"
                              >
                                <img
                                  src={f.url}
                                  alt={f.nome || 'Foto do parâmetro'}
                                  className="h-16 w-16 rounded border object-cover"
                                />
                                {f.nome ? (
                                  <span className="block max-w-[4rem] truncate text-[10px] text-gray-500">{f.nome}</span>
                                ) : null}
                              </a>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default RelatorioProducaoPage;
